import { useTranslation } from 'react-i18next'
import { HeroPage } from '@/components/HeroPage'
import { Section } from '@/components/ui/Section'
import { Container } from '@/components/ui/Container'
import { IconList } from '@/components/ui/IconList'
import { ContactCTA } from '@/components/sections/ContactCTA'

const productData: Record<string, {
  translationKey: string
  backgroundImage: string
  featureKeys?: string[]
  images?: string[]
}> = {
  'product-outsourcing': {
    translationKey: 'outsourcing',
    backgroundImage: '/images/products/header_produzione_estera.jpg',
    featureKeys: ['china', 'quality', 'logistics', 'savings'],
  },
  'product-machining': {
    translationKey: 'machining',
    backgroundImage: '/images/products/lavorazioni_meccaniche.jpg',
    featureKeys: ['turning', 'milling', 'grinding', 'finishing'],
  },
  'product-transmission': {
    translationKey: 'transmission',
    backgroundImage: '/images/products/organi_trasmissione.jpg',
    featureKeys: ['gears', 'shafts', 'flanges', 'custom'],
  },
  'product-plastics': {
    translationKey: 'plastics',
    backgroundImage: '/images/products/materie_plastiche.jpg',
    featureKeys: ['injection', 'compression', 'machining', 'assembly'],
  },
  'product-assembly': {
    translationKey: 'assembly',
    backgroundImage: '/images/general/hero_assemblaggi.jpg',
    featureKeys: ['mechanical', 'testing', 'packaging', 'logistics'],
  },
}

interface ProductDetailProps {
  productId: string
}

export function ProductDetail({ productId }: ProductDetailProps) {
  const { t } = useTranslation()

  const product = productId ? productData[productId] : null

  if (!product) {
    return (
      <main className="pt-32">
        <Container>
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold text-dark">{t('products.notFound')}</h1>
          </div>
        </Container>
      </main>
    )
  }

  const baseKey = `products.${product.translationKey}`

  return (
    <main>
      <HeroPage
        title={t(`${baseKey}.hero.title`)}
        subtitle={t(`${baseKey}.hero.subtitle`)}
        backgroundImage={product.backgroundImage}
        breadcrumb={[
          { label: t('nav.products'), to: 'products' },
          { label: t(`${baseKey}.hero.title`) },
        ]}
      />

      <Section>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-600 mb-8">
                {t(`${baseKey}.description`)}
              </p>
              {product.featureKeys && (
                <IconList items={product.featureKeys.map(key => t(`${baseKey}.features.${key}`))} />
              )}
            </div>
            <div className="relative rounded-lg shadow-lg overflow-hidden">
              <img
                src={product.backgroundImage}
                alt={t(`${baseKey}.hero.title`)}
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </Container>
      </Section>

      <ContactCTA />
    </main>
  )
}
